import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { parseHtml } from "@components/HTMLParser/utils";
import { IconButton } from "@components/IconButton/IconButton";
import { ChevronIcon, InformationCircleIcon } from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { type IJointBillServicePeriodVoucher } from "@services/overview/types";
import { formatPrice } from "@utils/units";
import { JointBillVoucherAddVoucher } from "./JointBillVoucherAddVoucher";
import { JointBillVoucherSummary } from "./JointBillVoucherSummary";

interface IJointBillVoucherActiveBillDisplayProps {
  totalExpectedPayment?: number;
  vouchers?: IJointBillServicePeriodVoucher[];
  jointBillServiceId: number;
}

export const JointBillVoucherActiveBillDisplay: React.FC<
  IJointBillVoucherActiveBillDisplayProps
> = ({ totalExpectedPayment = 0, vouchers, jointBillServiceId }) => {
  const { t } = useTranslation();
  const [isAddVoucherExpanded, setIsAddVoucherExpanded] = useState(false);
  const hasVoucher = (vouchers?.length ?? 0) > 0;

  return (
    <React.Fragment>
      <span className="text-right text-lg font-semibold">
        {t("bill.voucher.totalExpectedPayment", {
          amount: formatPrice(totalExpectedPayment),
        })}
      </span>
      {!hasVoucher && (
        <React.Fragment>
          <div className="flex items-center justify-end text-right">
            <span className="">
              {parseHtml(t("bill.voucher.addVoucher.label"))}
            </span>
            <Tooltip content={t("bill.voucher.addVoucher.hint")}>
              <InformationCircleIcon className="ml-1 size-4 text-grey-dark" />
            </Tooltip>
            <IconButton
              className="ml-2"
              iconClassName={twMerge(
                "size-5 transition-transform",
                !isAddVoucherExpanded && "rotate-180",
              )}
              icon={ChevronIcon}
              onClick={() => setIsAddVoucherExpanded((prev) => !prev)}
              disableGroup
              aria-label={t("bill.voucher.addVoucher.hint")}
            />
          </div>
          <JointBillVoucherAddVoucher
            isOpen={isAddVoucherExpanded}
            jointBillServiceId={jointBillServiceId}
          />
        </React.Fragment>
      )}
      {hasVoucher && (
        <JointBillVoucherSummary
          vouchers={vouchers!}
          jointBillServiceId={jointBillServiceId}
        />
      )}
    </React.Fragment>
  );
};
